import React from 'react';
import { MessageLookup, PostmarkUsage } from '@/components/admin/EmailFailures';

const TONE = {
  postmark_accepted: 'bg-green-100 text-green-800',
  skipped_not_found: 'bg-slate-100 text-slate-700',
  skipped_disabled: 'bg-amber-100 text-amber-800',
  skipped_locked: 'bg-amber-100 text-amber-800',
};

export const ResetAttemptsPanel = ({ data, token }) => (
  <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="reset-attempts-panel">
    <h3 className="font-bold text-lg mb-1">Password-reset attempts (no addresses)</h3>
    <p className="text-xs text-muted-foreground mb-3">
      Every request to “Forgot password”, newest first. The page always shows the same generic answer; this is the only place that tells what happened.
    </p>
    {!data ? (
      <p className="text-sm text-muted-foreground">Loading…</p>
    ) : data.attempts.length === 0 ? (
      <p className="text-sm text-muted-foreground" data-testid="reset-attempts-empty">No reset requests recorded yet.</p>
    ) : (
      <table className="w-full text-sm" data-testid="reset-attempts-table">
        <thead className="text-xs uppercase text-muted-foreground text-left">
          <tr><th className="py-1 pr-3">Time (UTC)</th><th className="py-1 pr-3">Account</th><th className="py-1 pr-3">Outcome</th><th className="py-1 pr-3">Postmark MessageID</th><th className="py-1">Postmark</th></tr>
        </thead>
        <tbody>
          {data.attempts.map((a, i) => (
            <tr key={i} className="border-t border-border/40" data-testid={`reset-attempt-row-${i}`}>
              <td className="py-1 pr-3 font-mono text-xs">{String(a.attempted_at).slice(0, 19).replace('T', ' ')}</td>
              <td className="py-1 pr-3 font-mono">{a.account}</td>
              <td className="py-1 pr-3">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${TONE[a.outcome] || 'bg-red-100 text-red-800'}`} title={a.detail || ''}>{a.outcome}</span>
              </td>
              <td className="py-1 pr-3 font-mono text-xs break-all">{a.message_id || '—'}</td>
              <td className="py-1">{a.message_id ? <MessageLookup token={token} initialId={a.message_id} compact /> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
    {data?.compares && <p className="text-[11px] text-muted-foreground mt-2">Compares: {data.compares}</p>}
  </div>
);

export const PostmarkCard = ({ pm }) => {
  const ok = pm.configured && !pm.server_error && String(pm.environment_guard || '').startsWith('ok');
  return (
    <div className={`rounded-lg border p-3 sm:col-span-2 ${ok ? 'border-green-200 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="startup-check-postmark">
      <p className="font-semibold">Postmark — {ok ? 'ok' : 'PROBLEM'} · server <span className="font-mono" data-testid="postmark-server-name">{pm.server_name || '—'}</span>{pm.server_id ? <> (ID <span className="font-mono" data-testid="postmark-server-id">{pm.server_id}</span>)</> : null} · {pm.delivery_type || '—'} · token from <span className="font-mono">{pm.token_source}</span></p>
      <PostmarkUsage usage={pm.usage} />
      <p className="text-xs mt-1" data-testid="postmark-guard">Environment guard: {pm.environment_guard}</p>
      {pm.server_error && <p className="text-xs text-red-700">Server lookup error: {JSON.stringify(pm.server_error)}</p>}
      <p className="text-xs mt-1">From {pm.from} · Reply-To {pm.reply_to} · stream {pm.stream} · webhook {pm.webhook_configured ? 'configured' : 'NOT configured'}</p>
      <p className="text-xs mt-1" data-testid="postmark-last-outbound">
        Last outbound: {pm.last_outbound ? `${String(pm.last_outbound.attempted_at).slice(0, 16)} UTC · ${pm.last_outbound.ok ? 'accepted' : `FAILED (${pm.last_outbound.error?.kind || 'error'})`} · ${pm.last_outbound.subject}` : 'none recorded'}
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">Compares: {pm.compares}</p>
    </div>
  );
};
